import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { db } from '@/data/db';
import { queryKeys } from '@/lib/query-keys';
import { ReportsRepo } from '@/data/repos/reportsRepo';

/**
 * Prefetches company-related data for offline use when viewing a company.
 *
 * When a user navigates to a company detail screen, this hook proactively
 * caches:
 * - All sites for the company
 * - All units for each site
 * - Latest finalized report for each site (for reference)
 *
 * This ensures inspectors can work offline after viewing a company.
 */
export function usePrefetchCompanyData(companyId: string | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!companyId) return;

    const prefetch = async () => {
      try {
        // Prefetch sites for this company
        await queryClient.prefetchQuery({
          queryKey: queryKeys.sites.byCompany(companyId),
          queryFn: async () => {
            return db.sites
              .where('companyId')
              .equals(companyId)
              .filter((site) => !site.syncDeletedAt)
              .toArray();
          },
          staleTime: 5 * 60 * 1000, // 5 minutes
        });

        // Get sites to prefetch their related data
        const sites = await db.sites
          .where('companyId')
          .equals(companyId)
          .filter((site) => !site.syncDeletedAt)
          .toArray();

        // Prefetch units for each site
        for (const site of sites) {
          await queryClient.prefetchQuery({
            queryKey: queryKeys.units.bySite(site.id),
            queryFn: async () => {
              return db.units
                .where('siteId')
                .equals(site.id)
                .filter((unit) => !unit.syncDeletedAt)
                .toArray();
            },
            staleTime: 5 * 60 * 1000,
          });
        }

        // Prefetch latest finalized report for each site (for reference)
        for (const site of sites) {
          await queryClient.prefetchQuery({
            queryKey: queryKeys.reports.latestFinalizedBySite(site.id),
            queryFn: async () => {
              return ReportsRepo.getLatestFinalizedBySite(site.id);
            },
            staleTime: 5 * 60 * 1000,
          });
        }
      } catch {
        // Silently fail - prefetch is best-effort
      }
    };

    void prefetch();
  }, [companyId, queryClient]);
}
